import React, { useState, useEffect, useRef } from 'react';
import './ProjectsSection.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const ProjectsSection = ({ setLightbox }) => {
  const { t, i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const [projects, setProjects] = useState([]);
  const [visibleProjects, setVisibleProjects] = useState(4);
  const projectRefs = useRef([]);

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get('https://taamirnajd.com/api/projects' );
        if (response.data) {
          const formattedProjects = response.data.map((p) => ({
            ...p,
            title: {
              en: p.title_en,
              ar: p.title_ar,
            },
            images: p.images?.map(img => {
              if (typeof img === 'string') {
                return { url: img };
              }
              return { url: img.url || img.path || img.image || '' };
            }) || []
          }));

          setProjects(formattedProjects);
          localStorage.setItem('projectsData', JSON.stringify(formattedProjects));
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        const savedProjects = localStorage.getItem('projectsData');
        if (savedProjects) {
          setProjects(JSON.parse(savedProjects));
        }
      }
    };

    fetchProjects();
  }, []);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target.querySelector('.project-image');
            if (img && img.dataset.src) {
              img.style.backgroundImage = `url(${img.dataset.src})`;
              img.removeAttribute('data-src');
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    projectRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      projectRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [projects, showAll]);

  const openLightbox = (project) => {
    setLightbox({
      isOpen: true,
      images: project.images.map(img => img.url),
      currentIndex: 0,
      title: project.title[i18n.language] || project.title.en
    });
  };

  const toggleShowAll = () => {
    setShowAll(prev => !prev);
  };

  // Handle responsive project visibility
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1200) {
        setVisibleProjects(4);
      } else if (width >= 992) {
        setVisibleProjects(3);
      } else if (width >= 768) {
        setVisibleProjects(2);
      } else {
        setVisibleProjects(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="projects-section" id="projects">
      {/* Main container for consistent padding */}
      <div className="section-container">
        {/* Text Header */}
        <h2 className="vision-title">{t('projects.title')}</h2>
        <div className="vision-divider">
          <span className="divider-line"></span>
          <span className="divider-diamond">◆</span>
          <span className="divider-line"></span>
        </div>
        <p className="vision-subtitle">
          {t('projects.subtitle')}
        </p>

        {/* Projects Grid */}
        <div className="projects-grid">
          {projects.slice(0, showAll ? projects.length : visibleProjects * 2).map((project, index) => (
            <div 
              key={project.id}
              className="project-card"
              ref={el => projectRefs.current[index] = el}
            >
              <div className="project-image-wrapper" onClick={() => openLightbox(project)}>
                <div 
                  className="project-image" 
                  data-src={project.images[0]?.url}
                ></div>
              </div>
              
              <div className="project-info">
                <h3>{project.title[i18n.language] || project.title.en}</h3>
                <button className="view-project" onClick={() => openLightbox(project)}>
                  {t('projects.viewProject')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {projects.length > visibleProjects * 2 && (
        <div className="view-all-projects">
          <button onClick={toggleShowAll} className="btn">
            {showAll ? t('projects.showLess') : t('projects.showAll')}
            <i className={`fas fa-chevron-${showAll ? 'up' : 'down'}`}></i>
          </button>
        </div>
      )}
    </section>
  );
};

export default ProjectsSection;
